import { useEffect, useMemo } from 'react';

import { useAtom } from 'jotai';

import { yesterday, monthAgo } from '../../../../utils/time';
import { calculateDifferenceInDays, getPeriodName, reformatDateMMDDYYYY } from '../../../MarketingMixModeling/parts/time';
import {
  startDateProjectionAtom, endDateProjectionAtom,
  startDateBudgetAtom, endDateBudgetAtom,
  startDateBaselineAtom, endDateBaselineAtom,
} from '../../atoms';

export const useProjectionPeriod = () => {
  const [startDate, setStartDate] = useAtom(startDateProjectionAtom);
  const [endDate, setEndDate] = useAtom(endDateProjectionAtom);

  const projectionPeriod = useMemo(() => calculateDifferenceInDays(startDate, endDate), [startDate, endDate]);
  const projectionName = useMemo(() => getPeriodName(projectionPeriod, startDate, endDate), [projectionPeriod, startDate, endDate]);

  const onDateChange = ({ startDate: start, endDate: end }) => {
    setStartDate(start);
    setEndDate(end);
  };

  return {
    projectionPeriod,
    projectionName,
    startDate, endDate,
    onDateChange,
  };
};

export const useBudgetPeriod = () => {
  const [startDate, setStartDate] = useAtom(startDateBudgetAtom);
  const [endDate, setEndDate] = useAtom(endDateBudgetAtom);

  const budgetPeriod = useMemo(() => calculateDifferenceInDays(startDate, endDate), [startDate, endDate]);
  const budgetPeriodName = useMemo(() => getPeriodName(budgetPeriod, startDate, endDate), [budgetPeriod, startDate, endDate]);

  const onDateChange = ({ startDate: start, endDate: end }) => {
    setStartDate(start);
    setEndDate(end);
  };

  return {
    budgetPeriod,
    budgetPeriodName,
    startDate, endDate,
    onDateChange,
  };
};

export const useBaselinePeriod = () => {
  const [startDate, setStartDate] = useAtom(startDateBaselineAtom);
  const [endDate, setEndDate] = useAtom(endDateBaselineAtom);

  const onDateChange = ({ startDate: start, endDate: end }) => {
    setStartDate(start);
    setEndDate(end);
  };

  useEffect(() => {
    if (!startDate && !endDate) {
      setStartDate(monthAgo);
      setEndDate(yesterday);
    }
  }, []);

  const periodValue = useMemo(() => {
    if (!(startDate && endDate)) return;
    return `${reformatDateMMDDYYYY(startDate)} - ${reformatDateMMDDYYYY(endDate)}`;
  }, [startDate, endDate]);

  const loading = !(startDate && endDate);

  return {
    loading, startDate, endDate,
    onDateChange, periodValue,
  };
};
